import {
  defineExporter,
  runExport,
  useExportRegistration,
  ExportSource,
  ExportOutcome,
  ExporterDef,
} from './exportRegistry';

/**
 * One entry per screen that can export: its file name and its columns.
 *
 * To add export to a screen:
 *   1. add an entry here (file name + columns),
 *   2. call `useExport('<key>', { rows, ctx })` where the screen renders, and
 *   3. put `<ExportButton exportKey="<key>" />` in its Header actions.
 *
 * `ctx` carries anything a column needs to look up that is not on the row
 * itself (a vehicle's name from its id, the project's currency, a label map).
 * Row types are described by what the columns read, so a screen passes its own
 * objects as they are.
 */

import type { Task } from '../../services/TasksService';
import { ACCOUNT_EXPORTERS } from './accountExporters';

const yesNo = (v: unknown) => (v ? 'Yes' : 'No');
const activeLabel = (archived?: boolean) => (archived ? 'Archived' : 'Active');

// ── Vendors ──────────────────────────────────────────────────────────────────
interface VendorRow {
  name: string;
  description?: string;
  services?: unknown[];
  instagramHandle?: string;
  sourceEvents?: (string | { title: string })[];
  rating?: number;
  reviewCount?: number;
  priceRange?: string;
  verified?: boolean;
  featured?: boolean;
}

// ── Tasks ────────────────────────────────────────────────────────────────────
type TaskRow = Task;
interface TaskCtx {
  idLabel: (t: TaskRow) => string;
  assignees: (t: TaskRow) => string;
  due: (t: TaskRow) => string;
}

// ── Logistics ────────────────────────────────────────────────────────────────
interface VehicleRow { name: string; type: string; license_plate: string; color?: string; status: string; capacity: number; driver_id?: string }
interface DriverRow { name: string; phone: string; email?: string; vehicle_id?: string }
interface AssignmentRow {
  guest_name: string; vehicle_id: string; movement_type?: string; pickup_location: string;
  pickup_time: string; dropoff_location?: string; plus_ones: number; status: string;
}
interface LogisticsCtx {
  vehicleTypeLabel: (type: string) => string;
  driverName: (id?: string) => string;
  vehicleName: (id?: string) => string;
  time: (t: string) => string;
}

// ── Budget category (one entry per tab) ─────────────────────────────────────
interface CategoryVendorRow { vendor: string; service: string; cost: number; paid: number; status: string }
interface CategoryPaymentRow { vendor: string; date: string; amount: number; method: string; status: string; reference?: string; notes?: string }
interface CategorySummaryRow { category: string; planned: number; actual: number; paid: number }

export const EXPORTERS = {
  vendors: defineExporter<VendorRow>({
    fileName: 'vendors-export.csv',
    columns: [
      { header: 'Name', value: r => r.name },
      { header: 'Description', value: r => r.description },
      { header: 'Services', value: r => (r.services ?? []).map(s => (typeof s === 'string' ? s : (s as { name?: string })?.name ?? '')).join('; ') },
      { header: 'Instagram Handle', value: r => r.instagramHandle },
      { header: 'Source', value: r => (r.sourceEvents ?? []).map(e => (typeof e === 'string' ? e : e.title)).join('; ') },
      { header: 'Rating', value: r => r.rating },
      { header: 'Review Count', value: r => r.reviewCount },
      { header: 'Price Range', value: r => r.priceRange },
      { header: 'Verified', value: r => yesNo(r.verified) },
      { header: 'Featured', value: r => yesNo(r.featured) },
    ],
  }),

  tasks: defineExporter<TaskRow, TaskCtx>({
    fileName: 'activities-export.csv',
    columns: [
      { header: 'ID', value: (t, c) => c.idLabel(t) },
      { header: 'Task', value: t => t.task },
      { header: 'Priority', value: t => t.priority },
      { header: 'Status', value: t => t.status },
      { header: 'Team', value: t => t.ownerTeam },
      { header: 'Assignee', value: (t, c) => c.assignees(t) },
      { header: 'Due', value: (t, c) => c.due(t) },
      { header: 'Milestone', value: t => t.milestone },
    ],
  }),

  'logistics-vehicles': defineExporter<VehicleRow, LogisticsCtx>({
    fileName: 'logistics-vehicles-export.csv',
    columns: [
      { header: 'Name', value: v => v.name },
      { header: 'Type', value: (v, c) => c.vehicleTypeLabel(v.type) },
      { header: 'License Plate', value: v => v.license_plate },
      { header: 'Color', value: v => v.color },
      { header: 'Status', value: v => v.status },
      { header: 'Capacity', value: v => v.capacity },
      { header: 'Driver', value: (v, c) => c.driverName(v.driver_id) },
    ],
  }),
  'logistics-drivers': defineExporter<DriverRow, LogisticsCtx>({
    fileName: 'logistics-drivers-export.csv',
    columns: [
      { header: 'Name', value: d => d.name },
      { header: 'Phone', value: d => d.phone },
      { header: 'Email', value: d => d.email },
      { header: 'Assigned Vehicle', value: (d, c) => c.vehicleName(d.vehicle_id) },
    ],
  }),
  'logistics-assignments': defineExporter<AssignmentRow, LogisticsCtx>({
    fileName: 'logistics-assignments-export.csv',
    columns: [
      { header: 'Guest', value: a => a.guest_name },
      { header: 'Vehicle', value: (a, c) => c.vehicleName(a.vehicle_id) },
      { header: 'Movement', value: a => a.movement_type ?? 'pickup' },
      { header: 'Pickup Location', value: a => a.pickup_location },
      { header: 'Pickup Time', value: (a, c) => c.time(a.pickup_time) },
      { header: 'Drop-off Location', value: a => a.dropoff_location },
      { header: 'Guests', value: a => 1 + a.plus_ones },
      { header: 'Status', value: a => a.status },
    ],
  }),

  inventory: defineExporter<
    { name: string; item_type: string | null; category: string | null; vendor: string | null; stock: number | string; unit_price: string | null; currency: string | null; inventory_status: string | null; last_movement_at: string | null; archived_at: string | null }
  >({
    fileName: 'inventory-export.csv',
    columns: [
      { header: 'Name', value: i => i.name },
      { header: 'Type', value: i => i.item_type },
      { header: 'Category', value: i => i.category },
      { header: 'Vendor', value: i => i.vendor },
      { header: 'On Hand', value: i => i.stock },
      { header: 'Unit Price', value: i => i.unit_price },
      { header: 'Currency', value: i => i.currency },
      { header: 'Status', value: i => i.inventory_status },
      { header: 'Last Movement', value: i => i.last_movement_at },
      { header: 'Archived', value: i => yesNo(i.archived_at) },
    ],
  }),

  'budget-category-vendors': defineExporter<CategoryVendorRow, { categoryTab: string }>({
    fileName: 'budget-category-vendors-export.csv',
    columns: [
      { header: 'Vendor', value: r => r.vendor },
      { header: 'For', value: r => r.service },
      { header: 'Cost', value: r => r.cost },
      { header: 'Paid', value: r => r.paid },
      { header: 'Remaining', value: r => r.cost - r.paid },
      { header: 'Status', value: r => r.status },
    ],
  }),
  'budget-category-payments': defineExporter<CategoryPaymentRow, { categoryTab: string }>({
    fileName: 'budget-category-payments-export.csv',
    columns: [
      { header: 'Vendor', value: r => r.vendor },
      { header: 'Date', value: r => r.date },
      { header: 'Amount', value: r => r.amount },
      { header: 'Method', value: r => r.method },
      { header: 'Status', value: r => r.status },
      { header: 'Reference', value: r => r.reference },
      { header: 'Notes', value: r => r.notes },
    ],
  }),
  'budget-category-summary': defineExporter<CategorySummaryRow, { categoryTab: string }>({
    fileName: 'budget-category-summary-export.csv',
    columns: [
      { header: 'Category', value: r => r.category },
      { header: 'Planned Amount', value: r => r.planned },
      { header: 'Actual Cost', value: r => r.actual },
      { header: 'Total Paid', value: r => r.paid },
      { header: 'Remaining', value: r => r.planned - r.actual },
    ],
  }),
  stationery: defineExporter<
    { name: string; category: string; status: string; quantityNeeded: number; quantityPrinted: number; costPerUnit: number; designer: string; printVendor: string; dueDate?: string; notes?: string },
    { categoryLabel: (c: string) => string; statusLabel: (s: string) => string }
  >({
    fileName: 'stationery-export.csv',
    columns: [
      { header: 'Item', value: it => it.name },
      { header: 'Category', value: (it, c) => c.categoryLabel(it.category) },
      { header: 'Status', value: (it, c) => c.statusLabel(it.status) },
      { header: 'Quantity Needed', value: it => it.quantityNeeded },
      { header: 'Quantity Printed', value: it => it.quantityPrinted },
      { header: 'Cost Per Unit', value: it => it.costPerUnit },
      { header: 'Designer', value: it => it.designer },
      { header: 'Print Vendor', value: it => it.printVendor },
      { header: 'Due Date', value: it => it.dueDate },
      { header: 'Notes', value: it => it.notes },
    ],
  }),

  music: defineExporter<
    { name: string; artist?: string; type: string; genre?: string; moment?: string; non_negotiable?: boolean; archived?: boolean; notes?: string },
    { tab: string }
  >({
    fileName: ctx => `music-${ctx.tab.toLowerCase().replace(/\s+/g, '-')}-export.csv`,
    columns: [
      { header: 'Song', value: s => s.name },
      { header: 'Artist', value: s => s.artist },
      { header: 'Category', value: s => s.type },
      { header: 'Genre', value: s => s.genre },
      { header: 'Moment', value: s => s.moment },
      { header: 'Non-Negotiable', value: s => yesNo(s.non_negotiable) },
      { header: 'Status', value: s => activeLabel(s.archived) },
      { header: 'Notes', value: s => s.notes },
    ],
  }),

  events: defineExporter<
    { id: string; title: string; category: string; status: string; date: string; time: string; location: string; headcount?: number; headcountConfirmed?: number; archived?: boolean },
    { id: (id: string) => string; date: (d: string) => string }
  >({
    fileName: 'events-export.csv',
    columns: [
      { header: 'ID', value: (e, c) => c.id(e.id) },
      { header: 'Title', value: e => e.title },
      { header: 'Category', value: e => e.category },
      { header: 'Status', value: e => e.status },
      { header: 'Date', value: (e, c) => c.date(e.date) },
      { header: 'Time', value: e => e.time },
      { header: 'Location', value: e => e.location },
      { header: 'Planned Headcount', value: e => e.headcount },
      { header: 'Confirmed Headcount', value: e => e.headcountConfirmed },
      { header: 'Archived', value: e => yesNo(e.archived) },
    ],
  }),

  guests: defineExporter<
    { id: string; name: string; side?: string | null; tags: { label: string }[]; rsvp: string; email: string; phone?: string; plus_ones: unknown[]; notes?: string; archived?: boolean },
    undefined
  >({
    fileName: 'guests-export.csv',
    columns: [
      { header: 'ID', value: g => g.id },
      { header: 'Name', value: g => g.name },
      { header: 'Side', value: g => g.side },
      { header: 'Tags', value: g => g.tags.map(t => t.label).join('; ') },
      { header: 'RSVP', value: g => g.rsvp },
      { header: 'Email', value: g => g.email },
      { header: 'Phone', value: g => g.phone },
      { header: 'Plus-ones', value: g => g.plus_ones.length },
      { header: 'Notes', value: g => g.notes },
      { header: 'Archived', value: g => yesNo(g.archived) },
    ],
  }),

  guidelines: defineExporter<
    { title: string; category: string; items: { text: string; emphasis?: boolean }[] },
    { categoryLabel: (c: string) => string }
  >({
    fileName: 'guidelines-export.csv',
    columns: [
      { header: 'Title', value: g => g.title },
      { header: 'Category', value: (g, c) => c.categoryLabel(g.category) },
      { header: 'Items', value: g => g.items.map(i => (i.emphasis ? `! ${i.text}` : i.text)).join(' · ') },
      { header: 'Item Count', value: g => g.items.length },
    ],
  }),

  calendar: defineExporter<{ title: string; date: string; time: string; type: string; detail: string }>({
    fileName: 'calendar-export.csv',
    columns: [
      { header: 'Title', value: r => r.title },
      { header: 'Date', value: r => r.date },
      { header: 'Time', value: r => r.time },
      { header: 'Type', value: r => r.type },
      { header: 'Location/Status', value: r => r.detail },
    ],
  }),

  venues: defineExporter<{
    name: string; address?: string; capacity?: number; archived?: boolean; amenities: string[];
    floorPlans: unknown[]; contactInfo?: { phone?: string; email?: string; website?: string };
  }>({
    fileName: 'venues-export.csv',
    columns: [
      { header: 'Name', value: v => v.name },
      { header: 'Address', value: v => v.address },
      { header: 'Capacity', value: v => v.capacity },
      { header: 'Status', value: v => activeLabel(v.archived) },
      { header: 'Amenities', value: v => v.amenities.join('; ') },
      { header: 'Floor Plans', value: v => v.floorPlans.length },
      { header: 'Phone', value: v => v.contactInfo?.phone },
      { header: 'Email', value: v => v.contactInfo?.email },
      { header: 'Website', value: v => v.contactInfo?.website },
    ],
  }),

  shipping: defineExporter<{ name: string; shipping_mode: string; active?: boolean; tracking_url?: string | null; currency: string | null; rate_per_cbm: string | null; rate_per_kg: string | null }>({
    fileName: 'shipping-export.csv',
    columns: [
      { header: 'Agent', value: a => a.name },
      { header: 'Mode', value: a => a.shipping_mode },
      { header: 'Rate Per CBM', value: a => a.rate_per_cbm },
      { header: 'Rate Per Kg', value: a => a.rate_per_kg },
      { header: 'Currency', value: a => a.currency },
      { header: 'Status', value: a => (a.active ? 'Active' : 'Inactive') },
      { header: 'Tracking URL', value: a => a.tracking_url },
    ],
  }),

  'procurement-orders': defineExporter<{
    id: string; marketplace_order_id: string | null; marketplace: string | null; seller_name: string | null; order_date: string | null; status: string;
    currency: string | null; total_amount: string | null; landing_cost: string | null; payment_status: string | null;
    quantity_ordered: number | string; quantity_received: number | string; receiving_status: string; shipment_count: number; archived_at: string | null;
  }>({
    fileName: 'procurement-orders-export.csv',
    columns: [
      { header: 'ID', value: o => o.id },
      { header: 'Order Number', value: o => o.marketplace_order_id },
      { header: 'Marketplace', value: o => o.marketplace },
      { header: 'Seller', value: o => o.seller_name },
      { header: 'Order Date', value: o => o.order_date },
      { header: 'Status', value: o => o.status },
      { header: 'Currency', value: o => o.currency },
      { header: 'Total', value: o => o.total_amount },
      { header: 'Estimated Landed Cost', value: o => o.landing_cost },
      { header: 'Payment Status', value: o => o.payment_status },
      { header: 'Quantity Ordered', value: o => o.quantity_ordered },
      { header: 'Quantity Received', value: o => o.quantity_received },
      { header: 'Receiving', value: o => o.receiving_status },
      { header: 'Shipments', value: o => o.shipment_count },
      { header: 'Archived', value: o => yesNo(o.archived_at) },
    ],
  }),

  'procurement-shipments': defineExporter<{
    id: string; status: string; progress_status: string; mode: string | null; leg: string | null; shipping_agent_name: string | null; waybill_no: string | null;
    container_ref: string | null; estimated_arrival: string | null; actual_arrival: string | null; total_cbm: string | number | null; total_cartons: number | null;
    freight_amount: string | null; freight_currency: string | null; order_count: number; order_refs: string[];
  }>({
    fileName: 'procurement-shipments-export.csv',
    columns: [
      { header: 'ID', value: s => s.id },
      { header: 'Progress', value: s => s.progress_status },
      { header: 'Status', value: s => s.status },
      { header: 'Mode', value: s => s.mode },
      { header: 'Leg', value: s => s.leg },
      { header: 'Agent', value: s => s.shipping_agent_name },
      { header: 'Waybill', value: s => s.waybill_no },
      { header: 'Container', value: s => s.container_ref },
      { header: 'Estimated Arrival', value: s => s.estimated_arrival },
      { header: 'Actual Arrival', value: s => s.actual_arrival },
      { header: 'CBM', value: s => s.total_cbm },
      { header: 'Cartons', value: s => s.total_cartons },
      { header: 'Freight', value: s => s.freight_amount },
      { header: 'Freight Currency', value: s => s.freight_currency },
      { header: 'Orders', value: s => s.order_refs.join('; ') },
    ],
  }),

  payments: defineExporter<
    { vendor: string; category: string; totalCost: number; totalPaid: number; balance: number; paymentStatus: string; lastPaymentDate: string | null },
    { statusLabel: (s: string) => string; date: (d: string | null) => string }
  >({
    fileName: 'payments-export.csv',
    columns: [
      { header: 'Vendor', value: r => r.vendor },
      { header: 'Category', value: r => r.category },
      { header: 'Total Cost', value: r => r.totalCost },
      { header: 'Paid', value: r => r.totalPaid },
      { header: 'Balance', value: r => r.balance },
      { header: 'Status', value: (r, c) => c.statusLabel(r.paymentStatus) },
      { header: 'Last Payment', value: (r, c) => c.date(r.lastPaymentDate) },
    ],
  }),

  contributors: defineExporter<
    { name: string; email?: string; phone?: string; role?: string; team_member_name?: string | null; total_contributed: number; reimbursement_status: string; net_balance: number; items_paid_for: unknown[]; archived?: boolean },
    { money: (amount: number) => string }
  >({
    fileName: 'contributors-export.csv',
    columns: [
      { header: 'Name', value: c => c.name },
      { header: 'Relationship', value: c => c.role },
      { header: 'Linked team member', value: c => c.team_member_name },
      { header: 'Email', value: c => c.email },
      { header: 'Phone', value: c => c.phone },
      { header: 'Amount', value: (c, ctx) => ctx.money(Number(c.total_contributed)) },
      { header: 'Net balance', value: (c, ctx) => ctx.money(Number(c.net_balance)) },
      { header: 'Reimbursement status', value: c => c.reimbursement_status },
      { header: 'Items', value: c => c.items_paid_for.length },
      { header: 'State', value: c => activeLabel(c.archived) },
    ],
  }),

  quotes: defineExporter<
    { vendorName: string; categoryId: string; eventIds: string[]; status: string; totalAmount: number; dateReceived?: string; notes?: string },
    { categoryName: (id: string) => string; eventNames: (ids: string[]) => string }
  >({
    fileName: 'quotes-export.csv',
    columns: [
      { header: 'Vendor', value: q => q.vendorName },
      { header: 'Category', value: (q, c) => c.categoryName(q.categoryId) },
      { header: 'Events', value: (q, c) => c.eventNames(q.eventIds) },
      { header: 'Status', value: q => q.status },
      { header: 'Total Amount', value: q => q.totalAmount },
      { header: 'Date Received', value: q => q.dateReceived },
      { header: 'Notes', value: q => q.notes },
    ],
  }),

  catalogue: defineExporter<
    { name: string; types: string[]; suppliers: unknown[]; customizable?: boolean; archived?: boolean },
    { currency: string; typeLabel: (t: string) => string; price: (p: any) => string; stock: (p: any) => number }
  >({
    fileName: 'catalogue-export.csv',
    columns: [
      { header: 'Name', value: p => p.name },
      { header: 'Type', value: (p, c) => p.types.map(c.typeLabel).join('; ') },
      { header: 'Price', value: (p, c) => c.price(p) },
      { header: 'Currency', value: (_p, c) => c.currency },
      { header: 'Stock', value: (p, c) => c.stock(p) },
      { header: 'Suppliers', value: p => p.suppliers.length },
      { header: 'Customizable', value: p => yesNo(p.customizable) },
      { header: 'Archived', value: p => yesNo(p.archived) },
    ],
  }),
  ...ACCOUNT_EXPORTERS,
} as const;

export type ExporterKey = keyof typeof EXPORTERS;

type RowOf<K extends ExporterKey> = (typeof EXPORTERS)[K] extends ExporterDef<infer R, any> ? R : never;
type CtxOf<K extends ExporterKey> = (typeof EXPORTERS)[K] extends ExporterDef<any, infer C> ? C : never;
export type SourceOf<K extends ExporterKey> = ExportSource<RowOf<K>, CtxOf<K>>;

/** A screen's current rows, for its Export button. Call once where the screen renders. */
export function useExport<K extends ExporterKey>(key: K, source: SourceOf<K>): void {
  useExportRegistration(key, source);
}

/** Run the export for a screen (what the Export button does). */
export function exportScreen(key: ExporterKey): ExportOutcome {
  return runExport(key, k => (EXPORTERS as Record<string, ExporterDef<any, any>>)[k]);
}

export { activeLabel };
